import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, ArrowRight, Tag } from "@phosphor-icons/react";

const ClientsPage = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    type: "individual",
    tax_id: "",
    address: "",
    tags: "",
    notes: "",
  });

  const load = async () => {
    const r = await api.get("/clients");
    setItems(r.data.items);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/clients", {
        ...form,
        tags: form.tags
          ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : [],
      });
      toast.success("Client added.");
      setShowNew(false);
      setForm({
        name: "",
        email: "",
        phone: "",
        type: "individual",
        tax_id: "",
        address: "",
        tags: "",
        notes: "",
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create client");
    }
  };

  const filtered = items.filter(
    (c) =>
      !q ||
      c.name.toLowerCase().includes(q.toLowerCase()) ||
      c.email?.toLowerCase().includes(q.toLowerCase()) ||
      (c.tags || []).some((t) => t.toLowerCase().includes(q.toLowerCase())),
  );

  return (
    <div data-testid="clients-page" className="space-y-8">
      <div className="flex items-end justify-between pb-8 border-b border-[#1A2F24]/12">
        <div>
          <p className="label-eyebrow">CRM</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#112018] mt-2">Clients</h1>
          <p className="text-[#606C64] mt-2 text-sm max-w-xl">
            Long-lived client records. One client can hold many engagements over years.
          </p>
        </div>
        <button
          data-testid="new-client-btn"
          onClick={() => setShowNew(!showNew)}
          className="btn-primary"
        >
          <Plus size={14} className="inline mr-2" /> New Client
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#606C64]"
          />
          <input
            data-testid="client-search"
            className="input-clean pl-10"
            placeholder="Search by name, email, tag…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <p className="label-eyebrow">{filtered.length} clients</p>
      </div>

      {showNew && (
        <form
          onSubmit={create}
          className="card-flat space-y-4 fade-up"
          data-testid="new-client-form"
        >
          <h2 className="font-serif text-2xl text-[#112018]">New Client</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Name" testid="client-name" required
              value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="Email" testid="client-email" type="email"
              value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Phone" testid="client-phone"
              value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <div>
              <label className="label-eyebrow block mb-2">Type</label>
              <select
                data-testid="client-type"
                className="input-clean"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="individual">Individual</option>
                <option value="business">Business</option>
              </select>
            </div>
            <Field label="Tax ID / EIN" testid="client-tax-id"
              value={form.tax_id} onChange={(v) => setForm({ ...form, tax_id: v })} />
            <Field label="Tags (comma-separated)" testid="client-tags"
              value={form.tags} onChange={(v) => setForm({ ...form, tags: v })}
              hint="e.g. priority, annual, audit" />
            <div className="md:col-span-2">
              <label className="label-eyebrow block mb-2">Address</label>
              <input
                data-testid="client-address"
                className="input-clean"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="label-eyebrow block mb-2">Notes</label>
              <textarea
                data-testid="client-notes"
                rows={3}
                className="input-clean"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" data-testid="client-submit">
              Save Client
            </button>
            <button
              type="button"
              onClick={() => setShowNew(false)}
              className="btn-outline"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {filtered.length === 0 ? (
        <div className="card-flat text-center py-16">
          <p className="font-serif text-2xl text-[#112018]">No clients found.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <Link
              key={c.id}
              to={`/clients/${c.id}`}
              data-testid={`client-card-${c.id}`}
              className="card-flat hover:translate-y-[-2px] transition-transform"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="label-eyebrow">{c.type}</p>
                  <p className="font-serif text-2xl text-[#112018] mt-1 truncate">
                    {c.name}
                  </p>
                  {c.email && (
                    <p className="text-sm text-[#606C64] mt-1 truncate">{c.email}</p>
                  )}
                </div>
                <ArrowRight size={16} className="text-[#A8977B] shrink-0 mt-1" />
              </div>
              {c.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {c.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#1A2F24] text-[#F7F6F2] text-[10px] font-mono uppercase tracking-widest"
                    >
                      <Tag size={9} /> {t}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-[#1A2F24]/12 grid grid-cols-2">
                <div>
                  <p className="label-eyebrow">Engagements</p>
                  <p className="font-mono text-lg text-[#1A2F24] mt-1">
                    {c.engagements_count}
                  </p>
                </div>
                <div>
                  <p className="label-eyebrow">Lifetime fees</p>
                  <p className="font-mono text-lg text-[#A8977B] mt-1">
                    ${(c.total_fees || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, testid, value, onChange, required, type = "text", hint }) => (
  <div>
    <label className="label-eyebrow block mb-2">{label}</label>
    <input
      data-testid={testid}
      type={type}
      required={required}
      className="input-clean"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    {hint && <p className="label-eyebrow mt-1 opacity-60">{hint}</p>}
  </div>
);

export default ClientsPage;
